import { CdkDrag } from '@angular/cdk/drag-drop';
import { AfterViewInit, Directive, ElementRef, inject, input, NgZone, Renderer2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';

type ResizeDirection = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

@Directive({
  selector: '[coinResizable]',
  host: {
    '(mousedown)': 'onMouseDown($event)'
  }
})
export class ResizableDirective implements AfterViewInit {
  private el = inject(ElementRef);
  private renderer = inject(Renderer2);
  private zone = inject(NgZone);

  minWidth = input<number>(300);
  minHeight = input<number>(200);
  drag = input<CdkDrag>();

  private resizing = false;
  private direction: ResizeDirection | null = null;
  private lastMouseX = 0;
  private lastMouseY = 0;
  private lastLeft = 0;
  private lastTop = 0;

  constructor() {
    this.zone.runOutsideAngular(() => {
      fromEvent(document, 'mousemove')
        .pipe(takeUntilDestroyed())
        .subscribe((e: MouseEvent) => this.onMouseMove(e));
      fromEvent(document, 'mouseup')
        .pipe(takeUntilDestroyed())
        .subscribe(() => this.stopResize());
    });
  }

  ngAfterViewInit() {
    this.setInitialSize();
  }

  private setInitialSize() {
    const el = this.el.nativeElement;
    const width = el.offsetWidth;
    const height = el.offsetHeight;

    // Ensure it's at least minWidth / minHeight
    el.style.width = `${Math.max(this.minWidth(), width)}px`;
    el.style.height = `${Math.max(this.minHeight(), height)}px`;
  }

  // Detect mousedown on resize handle
  onMouseDown(event: MouseEvent) {
    const handle = (event.target as HTMLElement).closest('.resize-handle');
    if (!handle) return;

    const direction = (handle as HTMLElement).dataset.direction as ResizeDirection;
    if (!direction) return;

    event.preventDefault();
    this.resizing = true;
    this.direction = direction;
    this.lastMouseX = event.clientX;
    this.lastMouseY = event.clientY;

    this.renderer.addClass(document.body, 'resizing');
  }

  // Mouse move to resize
  onMouseMove(event: MouseEvent) {
    if (!this.resizing || !this.direction) return;

    const dx = event.clientX - this.lastMouseX;
    const dy = event.clientY - this.lastMouseY;

    const el = this.el.nativeElement;
    let newWidth = el.offsetWidth;
    let newHeight = el.offsetHeight;
    // get if exists on .chatbot-overlay-panel transform: translate3d(-600px, 378px, 0px) <- to know where it was dragged
    const panel = document.querySelector('.chatbot-overlay-panel') as HTMLElement | null;
    const transform = panel ? window.getComputedStyle(panel).transform : null;
    if (transform && transform !== 'none') {
      const match = transform.match(/matrix\((.+)\)/);
      if (match) {
        const values = match[1].split(', ');
        this.lastLeft = parseFloat(values[4]);
        this.lastTop = parseFloat(values[5]);
      }
    } else {
      this.lastLeft = 0;
      this.lastTop = 0;
    }

    // Only handle bottom-right
    if (this.direction === 'bottom-right') {
      newWidth += dx;
      newHeight += dy;
      this.lastLeft += dx;
    }

    // Constrain to min size and viewport
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    newWidth = Math.max(this.minWidth(), Math.min(newWidth, viewportWidth - el.offsetLeft));
    newHeight = Math.max(this.minHeight(), Math.min(newHeight, viewportHeight - el.offsetTop));

    el.style.width = `${newWidth}px`;
    el.style.height = `${newHeight}px`;

    this.lastMouseX = event.clientX;
    this.lastMouseY = event.clientY;

    // Reset CDK Drag memory so next drag starts at current transform
    const drag = this.drag();
    if (drag) {
      const dragRef = drag._dragRef;
      if (dragRef) {
        dragRef.setFreeDragPosition({ x: this.lastLeft, y: this.lastTop });
      }
    }
  }

  // Stop resizing
  stopResize() {
    this.resizing = false;
    this.direction = null;
    this.renderer.removeClass(document.body, 'resizing');
  }
}
